import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, getToken, setToken } from "./api";

const AuthCtx = createContext(null);
const IMPERSONATE_BACKUP_KEY = "moxyplay_su_backup";

function decodeJwtPayload(token) {
  if (!token) return null;
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const pad = b64 + "===".slice((b64.length + 3) % 4);
    return JSON.parse(atob(pad));
  } catch { return null; }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [impersonating, setImpersonating] = useState(false);

  const refresh = useCallback(async () => {
    if (!getToken()) {
      setUser(null);
      setTenant(null);
      setImpersonating(false);
      setLoading(false);
      return;
    }
    try {
      const [meRes, tRes] = await Promise.all([
        api.get("/auth/me"),
        api.get("/tenants/current").catch(() => ({ data: null })),
      ]);
      setUser(meRes.data);
      setTenant(tRes.data);
      const payload = decodeJwtPayload(getToken());
      setImpersonating(!!payload?.impersonated_by);
    } catch (e) {
      setToken(null);
      setUser(null);
      setTenant(null);
      setImpersonating(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setToken(data.token);
    setUser(data.user);
    setTenant(data.tenant);
    return data;
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    setTenant(null);
    localStorage.removeItem(IMPERSONATE_BACKUP_KEY);
    window.location.href = "/login";
  };

  // Super-admin only: swap the current session for an impersonated tenant JWT.
  // The current super token is stashed in localStorage so `stopImpersonation`
  // can restore it seamlessly.
  const impersonate = async (tenantId) => {
    const superToken = getToken();
    const { data } = await api.post(`/tenants/${tenantId}/impersonate`);
    try {
      localStorage.setItem(IMPERSONATE_BACKUP_KEY, JSON.stringify({ token: superToken, at: Date.now() }));
    } catch { /* private mode */ }
    setToken(data.token);
    // Force a full reload so all in-flight state (channels, schedules) refetches
    // under the new tenant identity.
    window.location.href = "/console/dashboard";
    return data;
  };

  const stopImpersonation = () => {
    try {
      const raw = localStorage.getItem(IMPERSONATE_BACKUP_KEY);
      if (raw) {
        const { token } = JSON.parse(raw);
        if (token) setToken(token);
      }
    } catch { /* noop */ }
    localStorage.removeItem(IMPERSONATE_BACKUP_KEY);
    window.location.href = "/console/admin/tenants";
  };

  // Apply tenant branding as CSS variable
  useEffect(() => {
    if (tenant?.branding?.primary_color) {
      document.documentElement.style.setProperty("--tenant-primary", tenant.branding.primary_color);
    }
  }, [tenant]);

  return (
    <AuthCtx.Provider value={{
      user, tenant, loading, login, logout, refresh, setTenant,
      impersonating, impersonate, stopImpersonation,
    }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  return useContext(AuthCtx);
}
